import { LockOutlined, UserOutlined } from "@ant-design/icons";
import {
  LoginFormTypes,
  LoginPageProps,
  useLink,
  useRouterContext,
  useRouterType,
  useTranslate,
} from "@refinedev/core";
import {
  Button,
  Card,
  CardProps,
  Checkbox,
  Col,
  Form,
  FormProps,
  Input,
  Layout,
  LayoutProps,
  Row,
  theme,
  Typography,
} from "antd";
import React, { useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  clearFieldErrorMessageOnChange,
  onlyLatinCharsDigitsSpecialSymbolsRule,
} from "../../../../utils/form.constants";
import useAuth from "../../../../utils/hooks/useAuth";
import { ThemedTitle } from "../../../themedLayout/title";
import {
  bodyStyles,
  buttonLoginStyle,
  buttonStyle,
  cardHeaderStyles,
  containerStyles,
  headStyles,
  layoutStyles,
  PageTitleStyles,
} from "./styles";
const { useToken } = theme;

type LoginProps = LoginPageProps<LayoutProps, CardProps, FormProps>;

export const LoginPage: React.FC<LoginProps> = ({
  forgotPasswordLink,
  rememberMe,
  contentProps,
  wrapperProps,
  renderContent,
  formProps,
  title,
}) => {
  const { token } = useToken();
  const [form] = Form.useForm<LoginFormTypes>();
  const translate = useTranslate();
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();
  const { displayMode } = useParams();

  const ActiveLink = routerType === "legacy" ? LegacyLink : Link;

  const { login, loadingLogin } = useAuth();

  const PageTitle =
    title === false ? null : (
      <div
        style={{
          ...PageTitleStyles,
        }}
      >
        {title ?? <ThemedTitle collapsed={false} />}
      </div>
    );

  const CardHeader = (
    <div
      style={{
        ...cardHeaderStyles,
        marginTop: "25px",
      }}
    >
      <Typography style={{ fontSize: "16px" }}>Sign in</Typography>
      {displayMode !== "admin" && (
        <ActiveLink
          style={{
            color: token.colorPrimaryTextHover,
            fontSize: "14px",
            marginLeft: "auto",
            paddingRight: "15px",
          }}
          to="/register"
        >
          {translate("pages.login.buttons.Registration", "Registration")}
        </ActiveLink>
      )}
    </div>
  );

  const onFinish = useCallback(
    (values: LoginFormTypes) => {
      login({
        email: values.email ?? "",
        password: values.password ?? "",
        isAdminLogin: displayMode === "admin",
      });
    },
    [displayMode, login]
  );

  const CardContent = (
    <Card
      title={CardHeader}
      headStyle={headStyles}
      bodyStyle={bodyStyles}
      style={{
        ...containerStyles,
      }}
      {...(contentProps ?? {})}
    >
      <Form<LoginFormTypes>
        layout="vertical"
        form={form}
        onFinish={onFinish}
        requiredMark={false}
        initialValues={{
          remember: false,
        }}
        {...formProps}
        onValuesChange={clearFieldErrorMessageOnChange(form)}
      >
        <Form.Item
          name="email"
          rules={[
            onlyLatinCharsDigitsSpecialSymbolsRule,
            { required: true, validateTrigger: "onSubmit" },
            {
              type: "email",
              message: translate(
                "pages.login.errors.validEmail",
                "Invalid email address"
              ),
              validateTrigger: "onSubmit",
            },
          ]}
        >
          <Input
            size="large"
            prefix={
              <UserOutlined
                className="site-form-item-icon"
                style={{ marginRight: "10px" }}
              />
            }
            placeholder={translate("pages.login.fields.email", "Email")}
            style={{ ...buttonLoginStyle }}
          />
        </Form.Item>
        <Form.Item
          name="password"
          rules={[
            onlyLatinCharsDigitsSpecialSymbolsRule,
            { required: true, validateTrigger: "onSubmit" },
          ]}
        >
          <Input
            type="password"
            prefix={
              <LockOutlined
                className="site-form-item-icon"
                style={{ marginRight: "10px" }}
              />
            }
            placeholder="Password"
            size="large"
            style={{ ...buttonLoginStyle }}
          />
        </Form.Item>
        {displayMode !== "admin" && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "24px",
            }}
          >
            {rememberMe ?? (
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox
                  style={{
                    fontSize: "15px",
                  }}
                >
                  {translate("pages.login.buttons.rememberMe", "Remember me")}
                </Checkbox>
              </Form.Item>
            )}
            {forgotPasswordLink ?? (
              <ActiveLink
                style={{
                  color: token.colorPrimaryTextHover,
                  fontSize: "15px",
                  marginLeft: "auto",
                }}
                to="/forgot-password"
              >
                {translate(
                  "pages.login.buttons.forgotPassword",
                  "Forgot password?"
                )}
              </ActiveLink>
            )}
          </div>
        )}
        <Row>
          <Col span={10} offset={7}>
            <Form.Item
              style={{
                marginTop: "14px",
                marginBottom: 0,
              }}
            >
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={loadingLogin}
                block
                style={{ ...buttonStyle }}
              >
                {translate("pages.login.signin", "Sign in")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );

  return (
    <Layout style={layoutStyles} {...(wrapperProps ?? {})}>
      <Row
        justify="center"
        align="middle"
        style={{
          height: "100vh",
        }}
      >
        <Col xs={22}>
          {renderContent ? (
            renderContent(CardContent, PageTitle)
          ) : (
            <>
              {PageTitle}
              {CardContent}
            </>
          )}
        </Col>
      </Row>
    </Layout>
  );
};
